import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Textarea,
  Typography,
  IconButton,
  Tooltip,
  Autocomplete,
  AutocompleteOption,
} from '@mui/joy';
import { useTheme } from '@mui/joy/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ChatIcon from '@mui/icons-material/Chat';
import BookIcon from '@mui/icons-material/Book';
import type {
  CongregationBatch,
  CongregationBatchInput,
  CongregationBatchStatus,
  CongregationBatchEnrollment,
  CongregationBatchEnrollmentInput,
  CongregationBatchEnrollmentStatus,
  CongregationCourse,
  CongregationMember,
} from '../../types/congregation';
import {
  listCongregationBatches,
  createCongregationBatch,
  updateCongregationBatch,
  updateCongregationBatchStatus,
  deleteCongregationBatch,
  listBatchEnrollments,
  enrollMemberInBatch,
  updateBatchEnrollmentStatus,
  deleteBatchEnrollment,
  fetchCongregationMasters,
  searchCongregationMembers,
  fetchAllAdminMembers,
  listMentorAssignedDevotees,
} from '../../api';
import { useSnackbarStore } from '../../store/useSnackbarStore';
import BatchPostFeed from '../../components/congregation/BatchPostFeed';
import type { CongregationMember as CongregationMemberType } from '../../types/congregation';

interface BatchDialogState {
  open: boolean;
  batch: CongregationBatch | null;
}

interface EnrollmentDialogState {
  open: boolean;
  batch: CongregationBatch | null;
}

const initialBatchDialogState: BatchDialogState = { open: false, batch: null };
const initialEnrollmentDialogState: EnrollmentDialogState = { open: false, batch: null };

const statusColors: Record<CongregationBatchStatus, 'neutral' | 'primary' | 'success' | 'danger'> = {
  PLANNED: 'neutral',
  ACTIVE: 'primary',
  COMPLETED: 'success',
  CANCELLED: 'danger',
};

const batchStatusOptions: CongregationBatchStatus[] = ['PLANNED', 'ACTIVE', 'COMPLETED', 'CANCELLED'];

const enrollmentStatusColors: Record<CongregationBatchEnrollmentStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  ENROLLED: 'neutral',
  COMPLETED: 'success',
  DROPPED: 'danger',
  TRANSFERRED: 'warning',
};

export default function MentorBatchesPage() {
  const { showSnackbar } = useSnackbarStore();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const heroBackground = isDark
    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)'
    : 'radial-gradient(120% 140% at 8% 0%, rgba(255, 218, 170, 0.2) 0%, rgba(255, 218, 170, 0) 55%), radial-gradient(120% 140% at 95% 5%, rgba(165, 197, 255, 0.2) 0%, rgba(165, 197, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(248, 250, 255, 1) 45%, rgba(244, 246, 252, 1) 100%)';
  const heroOverlay = isDark
    ? 'radial-gradient(120% 120% at 15% -10%, rgba(56, 189, 248, 0.14), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(251, 191, 36, 0.12), transparent 55%)'
    : 'radial-gradient(120% 120% at 15% -10%, rgba(255, 220, 160, 0.45), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(129, 158, 255, 0.28), transparent 55%)';
  const heroTitle = isDark ? 'rgba(248,250,252,0.94)' : 'text.primary';
  const heroBody = isDark ? 'rgba(226,232,240,0.8)' : 'text.secondary';
  const panelWarm = isDark
    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))'
    : 'linear-gradient(135deg, rgba(255, 237, 213, 0.9), rgba(255, 247, 231, 0.9))';
  const panelCool = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.95))'
    : 'linear-gradient(135deg, rgba(224, 243, 255, 0.8), rgba(241, 249, 255, 0.95))';
  const orbColor = isDark ? 'rgba(56, 189, 248, 0.18)' : 'rgba(255, 214, 153, 0.35)';
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [currentMember, setCurrentMember] = React.useState<CongregationMember | null>(null);
  const [activeTab, setActiveTab] = React.useState<number | string>(0);
  const [batches, setBatches] = React.useState<CongregationBatch[]>([]);
  const [courses, setCourses] = React.useState<CongregationCourse[]>([]);
  const [batchDialog, setBatchDialog] = React.useState<BatchDialogState>(initialBatchDialogState);
  const [enrollmentDialog, setEnrollmentDialog] = React.useState<EnrollmentDialogState>(initialEnrollmentDialogState);
  const [selectedBatch, setSelectedBatch] = React.useState<CongregationBatch | null>(null);
  const [formStatus, setFormStatus] = React.useState<CongregationBatchStatus>('PLANNED');
  const [enrollments, setEnrollments] = React.useState<CongregationBatchEnrollment[]>([]);
  const [memberSearch, setMemberSearch] = React.useState('');
  const [memberOptions, setMemberOptions] = React.useState<CongregationMember[]>([]);
  const [mentors, setMentors] = React.useState<CongregationMember[]>([]);
  const [assignedDevotees, setAssignedDevotees] = React.useState<CongregationMember[]>([]);
  const [loadingDevotees, setLoadingDevotees] = React.useState(false);
  const loadedRef = React.useRef(false);
  const batchSummary = React.useMemo(() => {
    const total = batches.length;
    const planned = batches.filter((batch) => batch.status === 'PLANNED').length;
    const active = batches.filter((batch) => batch.status === 'ACTIVE').length;
    const completed = batches.filter((batch) => batch.status === 'COMPLETED').length;
    const cancelled = batches.filter((batch) => batch.status === 'CANCELLED').length;
    const enrolled = batches.reduce((sum, batch) => sum + (batch.enrolledCount || 0), 0);
    return { total, planned, active, completed, cancelled, enrolled };
  }, [batches]);

  const loadData = React.useCallback(async () => {
    try {
      setLoading(true);
      console.log('[BATCHES] Starting to load batches data...');
      
      // Load batches and masters first (critical)
      const [batchesData, mastersData] = await Promise.all([
        (async () => {
          try {
            const result = await listCongregationBatches();
            console.log('[BATCHES] listCongregationBatches result:', result);
            return result;
          } catch (err) {
            console.error('[BATCHES] listCongregationBatches error:', err);
            throw err;
          }
        })(),
        (async () => {
          try {
            const result = await fetchCongregationMasters();
            console.log('[BATCHES] fetchCongregationMasters result:', result);
            return result;
          } catch (err) {
            console.error('[BATCHES] fetchCongregationMasters error:', err);
            throw err;
          }
        })(),
      ]);
      
      console.log('[BATCHES] Core API responses received:', { batchesData, mastersData });
      const batchesToSet = batchesData?.batches || [];
      console.log('[BATCHES] Extracted batches:', batchesToSet);
      console.log('[BATCHES] Number of batches:', batchesToSet.length);
      setBatches(batchesToSet);
      setCourses(mastersData?.courses || []);

      // Load mentors separately (non-critical for page display)
      try {
        const allMembersData = await fetchAllAdminMembers({ limit: 1000 });
        console.log('[BATCHES] fetchAllAdminMembers result:', allMembersData);
        
        // Filter members who have MENTOR role
        const mentorMembers = (allMembersData?.results || [])
          .filter((member: any) => member.roles?.includes('MENTOR'))
          .map((member: any) => ({
            id: member.id,
            displayName: member.displayName,
            ...member,
          }));
        setMentors(mentorMembers);
      } catch (mentorError) {
        console.warn('[BATCHES] Failed to load mentors (non-critical):', mentorError);
        // Don't fail the entire page load if mentors fail to load
        setMentors([]);
      }
    } catch (error) {
      console.error('[BATCHES] Error loading batches:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[BATCHES] Error details:', { error, message: errorMessage });
      showSnackbar(`Failed to load batches: ${errorMessage}`, 'danger');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  React.useEffect(() => {
    if (!loadedRef.current) {
      loadedRef.current = true;
      loadData();
    }
  }, []); // loadedRef ensures this only runs once

  const loadEnrollments = React.useCallback(async (batch: CongregationBatch) => {
    try {
      const data = await listBatchEnrollments(batch.id);
      setEnrollments(data.enrollments || []);
      setSelectedBatch(batch);
    } catch (error) {
      showSnackbar('Failed to load enrollments', 'danger');
    }
  }, [showSnackbar]);

  const handleBatchSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const payload: CongregationBatchInput = {
      name: String(formData.get('name') || '').trim(),
      courseId: parseInt(String(formData.get('courseId') || '0'), 10),
      mentorId: formData.get('mentorId') ? parseInt(String(formData.get('mentorId')), 10) : undefined,
      description: String(formData.get('description') || '').trim() || null,
      startDate: String(formData.get('startDate') || '').trim() || null,
      endDate: String(formData.get('endDate') || '').trim() || null,
      maxCapacity: formData.get('maxCapacity') ? parseInt(String(formData.get('maxCapacity')), 10) : null,
    };

    if (!payload.name) {
      showSnackbar('Batch name is required', 'warning');
      return;
    }

    if (!payload.courseId) {
      showSnackbar('Course is required', 'warning');
      return;
    }

    if (!batchDialog.batch && !payload.mentorId) {
      showSnackbar('Caretaker is required', 'warning');
      return;
    }

    try {
      setSaving(true);
      if (batchDialog.batch) {
        await updateCongregationBatch(batchDialog.batch.id, payload);
        if (batchDialog.batch.status !== formStatus) {
          await updateCongregationBatchStatus(batchDialog.batch.id, formStatus);
        }
        showSnackbar('Batch updated successfully', 'success');
      } else {
        await createCongregationBatch(payload);
        showSnackbar('Batch created successfully', 'success');
      }
      setBatchDialog(initialBatchDialogState);
      await loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save batch';
      showSnackbar(message, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (batch: CongregationBatch, newStatus: CongregationBatchStatus) => {
    try {
      await updateCongregationBatchStatus(batch.id, newStatus);
      showSnackbar('Batch status updated', 'success');
      await loadData();
      if (selectedBatch?.id === batch.id) {
        setSelectedBatch({ ...batch, status: newStatus });
      }
    } catch (error) {
      showSnackbar('Failed to update status', 'danger');
    }
  };

  const handleDeleteBatch = async (batch: CongregationBatch) => {
    if (!window.confirm(`Are you sure you want to delete "${batch.name}"?`)) {
      return;
    }

    try {
      await deleteCongregationBatch(batch.id);
      showSnackbar('Batch deleted', 'success');
      await loadData();
      if (selectedBatch?.id === batch.id) {
        setSelectedBatch(null);
        setEnrollments([]);
      }
    } catch (error) {
      showSnackbar('Failed to delete batch', 'danger');
    }
  };

  const searchMembers = React.useCallback(
    async (query: string) => {
      if (query.length < 2) {
        setMemberOptions([]);
        return;
      }

      try {
        const data = await searchCongregationMembers(query, { limit: 20 });
        setMemberOptions(data.results?.map(r => r.member) || []);
      } catch (error) {
        console.error('Failed to search members:', error);
      }
    },
    [],
  );

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (memberSearch) {
        searchMembers(memberSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch, searchMembers]);

  // Load assigned devotees when enrollment dialog opens
  React.useEffect(() => {
    if (enrollmentDialog.open) {
      setLoadingDevotees(true);
      (async () => {
        try {
          const data = await listMentorAssignedDevotees();
          console.log('Assigned devotees response:', data);
          setAssignedDevotees(data.devotees || []);
        } catch (error) {
          console.error('Failed to load assigned devotees:', error);
          setAssignedDevotees([]);
        } finally {
          setLoadingDevotees(false);
        }
      })();
    } else {
      // Reset when dialog closes
      setAssignedDevotees([]);
      setMemberSearch('');
      setMemberOptions([]);
    }
  }, [enrollmentDialog.open]);

  // Sync formStatus when batch dialog opens/closes
  React.useEffect(() => {
    if (batchDialog.batch) {
      setFormStatus(batchDialog.batch.status);
    } else {
      setFormStatus('PLANNED');
    }
  }, [batchDialog.batch]);

  const handleEnrollSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!enrollmentDialog.batch) return;

    const formData = new FormData(event.currentTarget);
    const memberIdStr = String(formData.get('memberId') || '');

    if (!memberIdStr) {
      showSnackbar('Please select a member', 'warning');
      return;
    }

    const payload: CongregationBatchEnrollmentInput = {
      memberId: parseInt(memberIdStr, 10),
      notes: String(formData.get('notes') || '').trim() || null,
    };

    try {
      setSaving(true);
      await enrollMemberInBatch(enrollmentDialog.batch.id, payload);
      showSnackbar('Member enrolled successfully', 'success');
      setEnrollmentDialog(initialEnrollmentDialogState);
      await loadEnrollments(enrollmentDialog.batch);
      await loadData(); // Refresh batch list to update enrollment count
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to enroll member';
      showSnackbar(message, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleEnrollmentStatusChange = async (
    enrollment: CongregationBatchEnrollment,
    newStatus: CongregationBatchEnrollmentStatus,
  ) => {
    if (!selectedBatch) return;

    try {
      await updateBatchEnrollmentStatus(selectedBatch.id, enrollment.id, newStatus);
      showSnackbar('Enrollment status updated', 'success');
      await loadEnrollments(selectedBatch);
    } catch (error) {
      showSnackbar('Failed to update enrollment status', 'danger');
    }
  };

  const handleDeleteEnrollment = async (enrollment: CongregationBatchEnrollment) => {
    if (!selectedBatch) return;
    if (!window.confirm(`Remove ${enrollment.memberName} from this batch?`)) {
      return;
    }

    try {
      await deleteBatchEnrollment(selectedBatch.id, enrollment.id);
      showSnackbar('Enrollment removed', 'success');
      await loadEnrollments(selectedBatch);
      await loadData(); // Refresh batch list to update enrollment count
    } catch (error) {
      showSnackbar('Failed to remove enrollment', 'danger');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size="md" />
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={{ xs: 2, sm: 3, md: 4 }}>
        <Card
          variant="outlined"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 'xl',
            borderColor: 'divider',
            boxShadow: 'lg',
            background: heroBackground,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: heroOverlay,
              opacity: 0.9,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: 180,
              height: 180,
              borderRadius: '50%',
              right: { xs: -60, md: -20 },
              top: { xs: 30, md: 10 },
              background: orbColor,
              filter: 'blur(6px)',
              animation: 'batches-float 8s ease-in-out infinite',
              '@keyframes batches-float': {
                '0%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-12px)' },
                '100%': { transform: 'translateY(0px)' },
              },
              pointerEvents: 'none',
            }}
          />
          <CardContent sx={{ position: 'relative' }}>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={{ xs: 2, sm: 2 }}
              >
                <Stack spacing={0.75}>
                  <Typography level="h2" sx={{ fontSize: { xs: '24px', md: '28px' }, fontWeight: 700, color: heroTitle }}>
                    My Batches
                  </Typography>
                  <Typography level="body-sm" sx={{ fontSize: { xs: '12px', md: '13px' }, maxWidth: 520, color: heroBody }}>
                    Manage your course batches, track enrollments, and keep communications flowing.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip variant="soft" color="primary">
                    {batchSummary.total} batches
                  </Chip>
                  <Chip variant="soft" color="success">
                    {batchSummary.active} active
                  </Chip>
                  <Chip variant="soft" color="neutral">
                    {batchSummary.enrolled} enrolled
                  </Chip>
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelWarm,
                    boxShadow: 'sm',
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Status overview
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      {batchSummary.planned} planned • {batchSummary.completed} completed
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      {batchSummary.cancelled} cancelled batches in history.
                    </Typography>
                  </CardContent>
                </Card>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelCool,
                    boxShadow: 'sm',
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Quick actions
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                      {selectedBatch && (
                        <Button
                          variant="outlined"
                          color="neutral"
                          onClick={() => {
                            setSelectedBatch(null);
                            setEnrollments([]);
                          }}
                          sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                          ← Back to Batches
                        </Button>
                      )}
                      <Button
                        startDecorator={<AddIcon />}
                        onClick={() => setBatchDialog({ open: true, batch: null })}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Create Batch
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Show batch list as responsive grid when no batch is selected */}
        {!selectedBatch && (
          <>
            {batches.length === 0 ? (
              <Sheet
                variant="soft"
                sx={{
                  borderRadius: 'lg',
                  p: { xs: 3, sm: 4, md: 6 },
                  textAlign: 'center',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  border: '2px dashed',
                  borderColor: 'divider',
                }}
              >
                <Stack spacing={2} alignItems="center">
                  <BookIcon sx={{ fontSize: 48, color: 'neutral.400' }} />
                  <Stack spacing={0.5} alignItems="center">
                    <Typography level="title-lg" color="neutral">
                      No Batches Created Yet
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                      Create your first batch to start managing course enrollments and communications.
                    </Typography>
                  </Stack>
                  <Button
                    startDecorator={<AddIcon />}
                    onClick={() => setBatchDialog({ open: true, batch: null })}
                    size="md"
                  >
                    Create Your First Batch
                  </Button>
                </Stack>
              </Sheet>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(auto-fill, minmax(280px, 1fr))',
                    md: 'repeat(auto-fill, minmax(320px, 1fr))',
                  },
                  gap: { xs: 2, sm: 2.5, md: 3 },
                }}
              >
                {batches.map((batch) => (
                  <Sheet
                    key={batch.id}
                    variant="outlined"
                    onClick={() => loadEnrollments(batch)}
                    sx={{
                      p: { xs: 2, sm: 2.5, md: 3 },
                      borderRadius: 'md',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      '&:hover': {
                        boxShadow: 'md',
                        borderColor: 'primary.500',
                        backgroundColor: 'rgba(0,0,0,0.01)',
                      },
                    }}
                  >
                    {/* Status Chip */}
                    <Box sx={{ mb: 1.5 }}>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={statusColors[batch.status]}
                        sx={{ fontWeight: 500 }}
                      >
                        {batch.status}
                      </Chip>
                    </Box>

                    {/* Batch Name */}
                    <Typography
                      level="title-lg"
                      sx={{
                        mb: 0.5,
                        fontSize: { xs: '16px', md: '18px' },
                        fontWeight: 700,
                        lineHeight: 1.4,
                      }}
                    >
                      {batch.name}
                    </Typography>

                    {/* Course Name */}
                    <Typography
                      level="body-sm"
                      color="neutral"
                      sx={{
                        mb: 1.5,
                        fontSize: { xs: '13px', md: '14px' },
                      }}
                    >
                      {batch.courseName}
                    </Typography>

                    {/* Dates */}
                    {batch.startDate && (
                      <Typography
                        level="body-xs"
                        color="neutral"
                        sx={{
                          mb: 1.5,
                          fontSize: { xs: '12px', md: '13px' },
                        }}
                      >
                        {new Date(batch.startDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                        {batch.endDate && (
                          <>
                            {' - '}
                            {new Date(batch.endDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </>
                        )}
                      </Typography>
                    )}

                    {/* Enrollment Progress */}
                    <Box sx={{ mb: 2, mt: 'auto', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography level="body-xs" color="neutral" sx={{ fontSize: '12px' }}>
                          Enrolled
                        </Typography>
                        <Chip
                          size="sm"
                          variant="soft"
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        >
                          {batch.enrolledCount || 0}
                          {batch.maxCapacity && `/${batch.maxCapacity}`}
                        </Chip>
                      </Stack>
                      {batch.maxCapacity && (
                        <Box
                          sx={{
                            width: '100%',
                            height: '6px',
                            borderRadius: '3px',
                            backgroundColor: 'background.level2',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${Math.min(100, ((batch.enrolledCount || 0) / batch.maxCapacity) * 100)}%`,
                              backgroundColor: batch.enrolledCount && batch.enrolledCount >= batch.maxCapacity ? 'danger.500' : 'primary.500',
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Action Buttons */}
                    <Stack
                      direction="row"
                      spacing={0.75}
                      justifyContent="space-between"
                      sx={{ mt: 2 }}
                    >
                      <Tooltip title="Enroll members">
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEnrollmentDialog({ open: true, batch });
                          }}
                          sx={{ flex: 1, fontSize: '18px' }}
                        >
                          <PersonAddIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit batch">
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="neutral"
                          onClick={(e) => {
                            e.stopPropagation();
                            setBatchDialog({ open: true, batch });
                          }}
                          sx={{ flex: 1, fontSize: '18px' }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete batch">
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBatch(batch);
                          }}
                          sx={{ flex: 1, fontSize: '18px' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Sheet>
                ))}
              </Box>
            )}
          </>
        )}

        {/* Batch Details Panel */}
        {selectedBatch && (
          <Sheet
            variant="outlined"
            sx={{
              borderRadius: 'lg',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 'calc(100vh - 300px)',
            }}
          >
            {/* Batch Header */}
            <Box sx={{
              p: { xs: 2, sm: 3, md: 3 },
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'rgba(0,0,0,0.02)',
            }}>
              <Stack spacing={{ xs: 1.25, sm: 1.5 }} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Stack spacing={0.5}>
                  <Typography
                    level="title-lg"
                    sx={{ fontSize: { xs: '18px', md: '20px' } }}
                  >
                    {selectedBatch.name}
                  </Typography>
                  <Typography level="body-sm" color="neutral">
                    {selectedBatch.courseName}
                    {selectedBatch.startDate && (
                      <>
                        {' • '}
                        {new Date(selectedBatch.startDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                        {selectedBatch.endDate && (
                          <>
                            {' - '}
                            {new Date(selectedBatch.endDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </>
                        )}
                      </>
                    )}
                  </Typography>
                </Stack>

                <Chip
                  size="sm"
                  variant="soft"
                  color={statusColors[selectedBatch.status]}
                  sx={{ fontWeight: 600 }}
                >
                  {selectedBatch.status}
                </Chip>
              </Stack>
            </Box>

            <Tabs
              value={activeTab}
              onChange={(_, value) => value !== null && setActiveTab(value as number)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                '& [role="tablist"]': {
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                }
              }}
            >
              <TabList
                sx={{
                  p: 0,
                  overflowX: { xs: 'auto', sm: 'visible' },
                }}
              >
                <Tab
                  sx={{
                    flex: { xs: 'auto', sm: 1 },
                    minWidth: { xs: 'auto', sm: 'auto' },
                    whiteSpace: 'nowrap',
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                  }}
                >
                  <GroupIcon sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '18px', md: '20px' } }} />
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>
                    Enrollments
                  </Box>
                  <Chip
                    size="sm"
                    variant="solid"
                    color="primary"
                    sx={{ ml: { xs: 0.5, sm: 1 }, minWidth: '32px' }}
                  >
                    {enrollments.length}
                  </Chip>
                </Tab>
                <Tab
                  sx={{
                    flex: { xs: 'auto', sm: 1 },
                    minWidth: { xs: 'auto', sm: 'auto' },
                    whiteSpace: 'nowrap',
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                  }}
                >
                  <ChatIcon sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '18px', md: '20px' } }} />
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>
                    Messages
                  </Box>
                </Tab>
              </TabList>

              {/* Enrollments Tab */}
              <TabPanel
                value={0}
                sx={{
                  p: { xs: 2, sm: 3, md: 3 },
                  overflow: 'auto',
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                  >
                    <Typography level="title-md">
                      Enrollments
                    </Typography>
                    <Button
                      size="sm"
                      startDecorator={<PersonAddIcon />}
                      onClick={() => setEnrollmentDialog({ open: true, batch: selectedBatch })}
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                      Enroll Members
                    </Button>
                  </Stack>

                  {enrollments.length === 0 ? (
                    <Sheet
                      variant="soft"
                      sx={{
                        py: { xs: 4, sm: 6 },
                        px: { xs: 2, sm: 4 },
                        textAlign: 'center',
                        borderRadius: 'md',
                        backgroundColor: 'rgba(0,0,0,0.02)',
                      }}
                    >
                      <Stack spacing={1.5} alignItems="center">
                        <GroupIcon sx={{ fontSize: 40, color: 'neutral.400' }} />
                        <Typography level="body-sm" color="neutral">
                          No members enrolled yet
                        </Typography>
                        <Button
                          size="sm"
                          onClick={() => setEnrollmentDialog({ open: true, batch: selectedBatch })}
                        >
                          Enroll First Member
                        </Button>
                      </Stack>
                    </Sheet>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(auto-fill, minmax(300px, 1fr))',
                          md: 'repeat(auto-fill, minmax(350px, 1fr))',
                        },
                        gap: { xs: 1.5, sm: 2, md: 2.5 },
                      }}
                    >
                      {enrollments.map((enrollment) => (
                        <Sheet
                          key={enrollment.id}
                          variant="outlined"
                          sx={{
                            p: { xs: 2, sm: 2.5 },
                            borderRadius: 'md',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: 'md',
                              borderColor: 'primary.500',
                            },
                          }}
                        >
                          <Stack spacing={1.5}>
                            {/* Member Name and Status */}
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                              <Stack spacing={0.5} sx={{ flex: 1 }}>
                                <Typography level="body-sm" fontWeight={700}>
                                  {enrollment.memberName}
                                </Typography>
                                <Typography
                                  level="body-xs"
                                  color="neutral"
                                  sx={{ fontSize: '11px' }}
                                >
                                  Enrolled {new Date(enrollment.enrolledAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })}
                                </Typography>
                              </Stack>
                              <Chip
                                size="sm"
                                variant="soft"
                                color={
                                  enrollment.status === 'COMPLETED' ? 'success' :
                                    enrollment.status === 'DROPPED' ? 'danger' :
                                      enrollment.status === 'TRANSFERRED' ? 'warning' :
                                        'primary'
                                }
                              >
                                {enrollment.status}
                              </Chip>
                            </Stack>

                            {/* Notes */}
                            {enrollment.notes && (
                              <Typography
                                level="body-xs"
                                color="neutral"
                                sx={{
                                  p: 1,
                                  backgroundColor: 'background.level1',
                                  borderRadius: 'sm',
                                  fontSize: '12px',
                                  fontStyle: 'italic',
                                }}
                              >
                                "{enrollment.notes}"
                              </Typography>
                            )}

                            {/* Status Select and Delete */}
                            <Stack spacing={1} sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                              <FormLabel sx={{ fontSize: '12px', fontWeight: 600 }}>
                                Change Status
                              </FormLabel>
                              <Select
                                value={enrollment.status}
                                onChange={(_, value) =>
                                  value && handleEnrollmentStatusChange(enrollment, value as CongregationBatchEnrollmentStatus)
                                }
                                size="sm"
                                sx={{ fontSize: { xs: '13px', md: '14px' } }}
                              >
                                <Option value="ENROLLED">Enrolled</Option>
                                <Option value="COMPLETED">Completed</Option>
                                <Option value="DROPPED">Dropped</Option>
                                <Option value="TRANSFERRED">Transferred</Option>
                              </Select>
                              <Button
                                size="sm"
                                variant="soft"
                                color="danger"
                                onClick={() => handleDeleteEnrollment(enrollment)}
                                fullWidth
                              >
                                Remove from Batch
                              </Button>
                            </Stack>
                          </Stack>
                        </Sheet>
                      ))}
                    </Box>
                  )}
                </Stack>
              </TabPanel>

              {/* Messages Tab */}
              <TabPanel
                value={1}
                sx={{
                  p: { xs: 2, sm: 3 },
                  overflow: 'auto',
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <BatchPostFeed
                  batchId={selectedBatch.id}
                  currentMember={currentMember}
                  canPost={true}
                  canPin={true}
                  onError={(error) => showSnackbar(error, 'danger')}
                  onSuccess={(message) => showSnackbar(message, 'success')}
                />
              </TabPanel>
            </Tabs>
          </Sheet>
        )}
      </Stack>

      {/* Batch Dialog */}
      <Modal
        open={batchDialog.open}
        onClose={() => setBatchDialog(initialBatchDialogState)}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <ModalDialog
          sx={{
            minWidth: 'min(95vw, 500px)',
            maxHeight: '95vh',
            overflowY: 'auto',
            p: { xs: 2.5, sm: 3 },
          }}
        >
          <ModalClose variant="plain" />
          <Typography level="title-lg" sx={{ fontSize: { xs: '18px', md: '20px' }, mb: 2.5 }}>
            {batchDialog.batch ? 'Edit Batch' : 'Create New Batch'}
          </Typography>
          <form onSubmit={handleBatchSubmit}>
            <Stack spacing={2.5}>
              <FormControl required>
                <FormLabel sx={{ fontSize: { xs: '13px', md: '14px' }, fontWeight: 600 }}>
                  Batch Name
                </FormLabel>
                <Input
                  name="name"
                  defaultValue={batchDialog.batch?.name || ''}
                  autoFocus
                  required
                  placeholder="e.g., Bhagavad Gita Level 1"
                  size="md"
                  sx={{ fontSize: { xs: '14px', md: '15px' } }}
                />
              </FormControl>

              <FormControl required>
                <FormLabel sx={{ fontSize: { xs: '13px', md: '14px' }, fontWeight: 600 }}>
                  Course
                </FormLabel>
                <Select
                  name="courseId"
                  defaultValue={batchDialog.batch?.courseId || undefined}
                  required
                  size="md"
                  sx={{ fontSize: { xs: '14px', md: '15px' } }}
                >
                  <Option value="">Select a course</Option>
                  {courses.map((course) => (
                    <Option key={course.id} value={course.id}>
                      {course.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl required>
                <FormLabel sx={{ fontSize: { xs: '13px', md: '14px' }, fontWeight: 600 }}>
                  Caretaker
                </FormLabel>
                <Select
                  name="mentorId"
                  defaultValue={batchDialog.batch?.mentorId ? String(batchDialog.batch.mentorId) : ''}
                  required
                  size="md"
                  sx={{ fontSize: { xs: '14px', md: '15px' } }}
                >
                  <Option value="">Select a caretaker</Option>
                  {mentors.map((mentor) => (
                    <Option key={mentor.id} value={String(mentor.id)}>
                      {mentor.displayName}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel sx={{ fontSize: { xs: '13px', md: '14px' }, fontWeight: 600 }}>
                  Status
                </FormLabel>
                <Select
                  value={formStatus}
                  onChange={(_, value) => value && setFormStatus(value as CongregationBatchStatus)}
                  size="md"
                  sx={{ fontSize: { xs: '14px', md: '15px' } }}
                >
                  {batchStatusOptions.map((status) => (
                    <Option key={status} value={status}>
                      {status}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel sx={{ fontSize: { xs: '13px', md: '14px' }, fontWeight: 600 }}>
                  Description
                </FormLabel>
                <Textarea
                  name="description"
                  defaultValue={batchDialog.batch?.description || ''}
                  minRows={3}
                  placeholder="Add a brief description about this batch..."
                  sx={{ fontSize: { xs: '14px', md: '15px' } }}
                />
              </FormControl>

              <Stack spacing={2}>
                <Typography level="body-xs" sx={{ textTransform: 'uppercase', fontWeight: 600, color: 'neutral.600', fontSize: '11px' }}>
                  Batch Timeline
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel sx={{ fontSize: { xs: '13px', md: '14px' } }}>
                      Start Date
                    </FormLabel>
                    <Input
                      name="startDate"
                      type="date"
                      defaultValue={batchDialog.batch?.startDate?.split('T')[0] || ''}
                      slotProps={{
                        input: { style: { fontSize: '14px' } }
                      }}
                    />
                  </FormControl>

                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel sx={{ fontSize: { xs: '13px', md: '14px' } }}>
                      End Date
                    </FormLabel>
                    <Input
                      name="endDate"
                      type="date"
                      defaultValue={batchDialog.batch?.endDate?.split('T')[0] || ''}
                      slotProps={{
                        input: { style: { fontSize: '14px' } }
                      }}
                    />
                  </FormControl>
                </Stack>
              </Stack>

              <FormControl>
                <FormLabel sx={{ fontSize: { xs: '13px', md: '14px' }, fontWeight: 600 }}>
                  Maximum Capacity
                </FormLabel>
                <Input
                  name="maxCapacity"
                  type="number"
                  defaultValue={batchDialog.batch?.maxCapacity || ''}
                  placeholder="e.g., 30"
                  slotProps={{ input: { min: 1, style: { fontSize: '14px' } } }}
                />
              </FormControl>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end" sx={{ pt: 2, mt: 1 }}>
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={() => setBatchDialog(initialBatchDialogState)}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={saving}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {batchDialog.batch ? 'Update Batch' : 'Create Batch'}
                </Button>
              </Stack>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>

      {/* Enrollment Dialog */}
      <Modal
        open={enrollmentDialog.open}
        onClose={() => setEnrollmentDialog(initialEnrollmentDialogState)}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <ModalDialog
          sx={{
            minWidth: 'min(95vw, 500px)',
            maxHeight: '95vh',
            overflowY: 'auto',
            p: { xs: 2.5, sm: 3 },
          }}
        >
          <ModalClose variant="plain" />
          <Typography level="title-lg" sx={{ fontSize: { xs: '18px', md: '20px' } }}>
            Enroll Member
          </Typography>
          <Typography level="body-sm" color="neutral" sx={{ mb: 2.5, fontSize: { xs: '13px', md: '14px' } }}>
            Add a devotee to {enrollmentDialog.batch?.name}
          </Typography>
          <form onSubmit={handleEnrollSubmit}>
            <Stack spacing={2.5}>
              <FormControl>
                <FormLabel sx={{ fontSize: { xs: '13px', md: '14px' }, fontWeight: 600, mb: 0.75 }}>
                  Assigned Devotees
                </FormLabel>
                {assignedDevotees.length > 0 ? (
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        const devotee = assignedDevotees.find(d => d.id === parseInt(e.target.value));
                        if (devotee) {
                          setMemberSearch(devotee.displayName);
                          setMemberOptions([devotee]);
                          e.target.value = '';
                        }
                      }
                    }}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      backgroundColor: 'var(--joy-palette-background-surface)',
                      color: 'var(--joy-palette-text-primary)',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">Select from assigned devotees...</option>
                    {assignedDevotees.map((devotee) => (
                      <option key={devotee.id} value={devotee.id}>
                        {devotee.displayName} {devotee.email ? `(${devotee.email})` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Typography level="body-sm" color="neutral" sx={{ py: 1.5, px: 2, bgcolor: 'neutral.50', borderRadius: 'md', fontSize: { xs: '13px', md: '14px' } }}>
                    No devotees assigned to you yet. Use search below to add members.
                  </Typography>
                )}
              </FormControl>

              <FormControl required>
                <FormLabel sx={{ fontSize: { xs: '13px', md: '14px' }, fontWeight: 600, mb: 0.75 }}>
                  Member
                </FormLabel>
                <Autocomplete
                  name="memberId"
                  placeholder="Search for member..."
                  options={memberOptions}
                  getOptionLabel={(option) => option.displayName}
                  inputValue={memberSearch}
                  onInputChange={(_, value) => setMemberSearch(value)}
                  renderOption={(props, option) => (
                    <AutocompleteOption {...props} key={option.id} value={option.id}>
                      <Stack>
                        <Typography level="body-sm" sx={{ fontSize: { xs: '13px', md: '14px' } }}>
                          {option.displayName}
                        </Typography>
                        {option.email && (
                          <Typography level="body-xs" color="neutral" sx={{ fontSize: { xs: '11px', md: '12px' } }}>
                            {option.email}
                          </Typography>
                        )}
                      </Stack>
                    </AutocompleteOption>
                  )}
                  required
                  size="md"
                  sx={{ fontSize: { xs: '14px', md: '15px' } }}
                />
              </FormControl>

              <FormControl>
                <FormLabel sx={{ fontSize: { xs: '13px', md: '14px' }, fontWeight: 600, mb: 0.75 }}>
                  Notes
                </FormLabel>
                <Textarea
                  name="notes"
                  minRows={3}
                  placeholder="Any additional notes about this enrollment..."
                  sx={{ fontSize: { xs: '14px', md: '15px' } }}
                />
              </FormControl>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end" sx={{ pt: 2, mt: 1 }}>
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={() => setEnrollmentDialog(initialEnrollmentDialogState)}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={saving}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Enroll Member
                </Button>
              </Stack>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
